"use client";

import { useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function NosPiliersDAction() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const [language, setLanguage] = useState<string>("fr");
  useEffect(() => {
    const getLang = () => {
      if (typeof window !== "undefined") {
        return localStorage.getItem("lacolonne_language") || "fr";
      }
      return "fr";
    };
    setLanguage(getLang());
    const handleLanguageChange = () => {
      setLanguage(getLang());
    };
    window.addEventListener("storage", handleLanguageChange);
    window.addEventListener("languagechange", handleLanguageChange);
    return () => {
      window.removeEventListener("storage", handleLanguageChange);
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, []);
  const isEnglish = language === "en";
  const pillars = [{
    numeral: "I",
    name: isEnglish ? "Who We Are" : "Qui sommes-nous",
    body: isEnglish ? "Association La Colonne is a francophone community organization based in Edmonton that works with francophone seniors from immigrant and visible minority backgrounds to foster well-being and inclusion." : "L'Association La Colonne est un organisme communautaire francophone basé à Edmonton qui œuvre auprès des aînés francophones issus de l'immigration et des minorités visibles pour favoriser le bien-être et l'inclusion.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/about_us_community.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/about_us_community.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/about_us_community.webp-wvc-sizes",
      alt: isEnglish ? "Association La Colonne members in a community meeting" : "Membres de l'Association La Colonne en réunion communautaire"
    },
    alignment: "justify-start"
  }, {
    numeral: "II",
    name: isEnglish ? "Mission" : "Mission",
    body: isEnglish ? "Promote the know-how, experience, and talents of diverse francophone seniors to foster their inclusion, active participation, and thriving within their host community." : "Promouvoir le savoir-faire, l'expérience et les talents des aînés francophones issus de la diversité afin de favoriser leur inclusion, leur participation active et leur épanouissement au sein de leur communauté d'accueil.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/local_image_cloud_media_3a9f1de2-e2e7-4b7f-a5bb-1c0752af9231_a7345447415ba5d4de710592a33cb246.jpg",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/local_image_cloud_media_3a9f1de2-e2e7-4b7f-a5bb-1c0752af9231_a7345447415ba5d4de710592a33cb246.jpg-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/local_image_cloud_media_3a9f1de2-e2e7-4b7f-a5bb-1c0752af9231_a7345447415ba5d4de710592a33cb246.jpg-wvc-sizes",
      alt: isEnglish ? "Seniors sharing their talents and experience" : "Aînés partageant leurs talents et expériences"
    },
    alignment: "justify-center"
  }, {
    numeral: "III",
    name: isEnglish ? "Vision" : "Vision",
    body: isEnglish ? "We envision an inclusive, supportive, and resilient community where diverse francophone seniors are valued, engaged, and fully thriving." : "Nous rêvons d'une communauté inclusive, solidaire et résiliente où les aînés francophones issus de la diversité sont valorisés, engagés et pleinement épanouis.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2026-06-01 at 6.18.01 PM.jpg",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2026-06-01 at 6.18.01 PM.jpg-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2026-06-01 at 6.18.01 PM.jpg-wvc-sizes",
      alt: isEnglish ? "Vision of a supportive and thriving community" : "Vision d'une communauté solidaire et épanouie"
    },
    alignment: "justify-center"
  }, {
    numeral: "IV",
    name: isEnglish ? "Values" : "Valeurs",
    body: isEnglish ? "Respect, inclusion, dignity, and community engagement guide our actions to build a caring environment and a stronger community." : "Le respect, l'inclusion, la dignité et l'engagement communautaire guident nos actions pour bâtir un environnement bienveillant et une communauté plus forte.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/112310_15d1623afa3023bda8e9117dac8bf63e-30_05_2026 22_59_18.jpg",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/112310_15d1623afa3023bda8e9117dac8bf63e-30_05_2026 22_59_18.jpg-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/112310_15d1623afa3023bda8e9117dac8bf63e-30_05_2026 22_59_18.jpg-wvc-sizes",
      alt: isEnglish ? "Francophone seniors engaged in community activities" : "Aînés francophones engagés dans des activités communautaires"
    },
    alignment: "justify-end"
  }];
  return <section data-section-id="211" id="nos-piliers-daction" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-36 px-6 md:px-10">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")"
    }} />

      <div className="relative mx-auto max-w-[1360px]">
        <div ref={introRef} className={`mx-auto max-w-4xl text-center transition-all duration-1000 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.25em] uppercase font-semibold" style={{
          color: "var(--muted-foreground)"
        }}>
            Association La Colonne —
          </p>
          <h2 className="font-default font-black uppercase text-foreground mt-4 text-5xl sm:text-6xl md:text-7xl lg:text-[86px] leading-[0.95] tracking-tight">
            {isEnglish ? <>
                About{" "}
                <em className="italic font-serif font-normal" style={{
              color: "var(--chart-1)"
            }}>
                  us
                </em>
                .
              </> : <>
                À{" "}
                <em className="italic font-serif font-normal" style={{
              color: "var(--chart-1)"
            }}>
                  propos
                </em>
                .
              </>}
          </h2>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => {
          const {
            ref,
            isVisible
          } = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div key={pillar.name} data-index={i} ref={ref} className={`group bg-card border border-border/80 rounded-2xl lg:rounded-3xl p-6 sm:p-7 flex flex-col justify-between shadow-md hover:shadow-lg transition-all duration-700 ease-out hover:-translate-y-1 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="font-serif font-light text-4xl sm:text-5xl leading-none select-none" style={{
                  color: "var(--chart-1)"
                }} aria-hidden="true">
                      {pillar.numeral}
                    </span>
                    <span className="h-2 w-2 rounded-full" style={{
                  backgroundColor: "var(--chart-1)"
                }} aria-hidden="true" />
                  </div>

                  <h3 className="font-default font-bold text-foreground mt-6 text-2xl md:text-[26px] leading-tight tracking-tight">
                    {pillar.name}
                  </h3>

                  <div className="mt-4 h-[2px] w-8 transition-all duration-300 group-hover:w-12" style={{
                backgroundColor: "var(--chart-1)"
              }} aria-hidden="true" />

                  <p className="font-default mt-5 text-[15px] sm:text-base leading-relaxed text-foreground/85">
                    {pillar.body}
                  </p>
                </div>

                <div className="mt-8">
                  <div className="w-full overflow-hidden rounded-xl border border-border/70 relative aspect-[4/3] sm:aspect-[4/5] bg-muted/30">
                    <img src={pillar.image.src} data-wvc-srcset={pillar.image.srcset} data-wvc-sizes={pillar.image.sizes} alt={pillar.image.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}